import { TextField } from '@nike/eds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import RewriteClient from '../../clientAPI/rewrite';

export default function RewriteTermInput({
  termValue,
  termErrorMessage,
  handleTermValueChange,
  hanldeTermValueValidation,
}: {
  termValue: string;
  termErrorMessage: string;
  handleTermValueChange: (value: string) => void;
  hanldeTermValueValidation: (termValue: string, validated: boolean) => void;
}) {
  const [localTerm, setLocalTerm] = useState(termValue);

  const debouncedHandleChange = useDebouncedCallback((value: string) => {
    handleTermValueChange(value);
  }, 800);

  const {
    data,
    isLoading: isNormalizing,
    isError,
  } = useQuery({
    queryKey: ['normalizeRewriteTerm', termValue],
    queryFn: async () => {
      const normalizedTerm = await RewriteClient.normalizeRewriteTerm(
        termValue,
      );
      const existingRewrite = await RewriteClient.getByRewriteTerm(
        normalizedTerm,
      );
      return {
        normalizedTerm,
        existingRewrite,
      };
    },
    enabled: !!termValue,
    onSuccess: (res: {
      normalizedTerm: string;
      existingRewrite: RewriteItem;
    }) => {
      hanldeTermValueValidation(termValue, res?.existingRewrite == null);
    },
    onError: () => {
      hanldeTermValueValidation(termValue, false);
    },
  });

  return (
    <div>
      <div>
        <TextField
          type='text'
          value={localTerm}
          onChange={(event) => {
            const val = (event.target as HTMLInputElement).value;
            setLocalTerm(val);
            debouncedHandleChange(val);
          }}
          id='rewrite-term-create'
          required
          placeholder='term'
          label=''
        />
      </div>
      {isNormalizing && termValue !== '' && (
        <div className='tw-text-gray-500 tw-text-sm tw-text-left tw-mt-2 tw-animate-flicker'>
          Validating term ...
          <style>{`
                @keyframes flicker {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.3; }
                }
                .tw-animate-flicker {
                  animation: flicker 1.2s ease-in-out infinite;
                }
              `}</style>
        </div>
      )}
      {isError && (
        <div className='tw-text-sm tw-text-left tw-mt-2'>
          <span style={{ color: 'red', fontWeight: 'bold' }}>&#10060;</span>{' '}
          Validation service error, please try again later.
        </div>
      )}
      {data?.normalizedTerm && data?.normalizedTerm !== termValue && (
        <div className='tw-text-gray-500 tw-text-sm tw-text-left tw-mt-2'>
          The text will be normalized to term
          <span className='tw-font-bold tw-text-black tw-p-1'>
            {data?.normalizedTerm}
          </span>
        </div>
      )}
      {!isNormalizing && data && !data.existingRewrite && termValue !== '' && (
        <div className='tw-text-sm tw-text-left tw-mt-2'>
          <span style={{ color: 'green', fontWeight: 'bold' }}>&#10004;</span>{' '}
          Term validated
        </div>
      )}
      {data?.existingRewrite && (
        <div className='tw-text-gray-500 tw-text-sm tw-text-left tw-mt-2'>
          <span style={{ color: 'red', fontWeight: 'bold' }}>&#10060;</span> The
          term
          <span className='tw-font-bold tw-text-black tw-p-1'>
            {data?.normalizedTerm}
          </span>
          already exists in the database.
        </div>
      )}
    </div>
  );
}
